import { useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import axios from 'axios';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { logSafeAxiosError } from 'src/api/axiosClient/errorHandling';
import { useGetMessageTemplatesApi } from 'src/api/messageTemplates';
import { GetMessageTemplatesParams } from 'src/api/messageTemplates/types';
import { useGetUsersApi } from 'src/api/users';
import { GetUsersParams } from 'src/api/users/types';
import { useGetContactCustomFieldsApi } from 'src/api/customFields';
import {
  ContactDefaultCustomField,
  FileLinkWithMimeType,
  InboxContact,
  InboxContactChatMessage,
  InboxNewMessage,
  MessageTemplate,
  User,
} from 'src/constants/types';
import { convertHtmlToText } from 'src/helpers/helpers';
import { isBhiAdmin, isPageForbidden } from 'src/helpers/permissions';
import { useCurrentCompany } from 'src/modules/companies/hooks';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useEditContactDrawerController } from 'src/modules/contacts/controllers/EditContactDrawerController';
import { useGroupsApi } from 'src/modules/groups/api';
import { Group } from 'src/modules/groups/types';
import { selectProfile } from 'src/modules/profile/slices';
import { useInboxApi, INBOX_CONTACTS_LIST_SCOPE, INBOX_CONTACT_CHAT_SCOPE } from '../api';
import { FilterType, filterPresets } from '../helpers';
import {
  useGetInboxContactChatService,
  useSetInboxChatAsReadService,
  useSetInboxMessageAsUnreadService,
  useLoadMoreMessagesService,
} from '../services';
import {
  getInboxChat,
  getInboxScheduledMessages,
  setChat,
  setScheduledMessages,
  getHasMoreMessages,
  getIsLoadingMoreMessages,
} from '../slices';
import { GetInboxContactsParams } from '../types';

const isRequestCanceled = (error: unknown) =>
  axios.isAxiosError(error) && error.code === 'ERR_CANCELED';

export const useInboxViewController = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedContact, setSelectedContact] = useState<string | null>(null);
  const [selectedContactObject, setSelectedContactObject] = useState<InboxContact | undefined>(
    undefined,
  );
  const [search, setSearch] = useState<string>('');
  const [showChatView, setShowChatView] = useState<boolean>(false);

  const isMobile = useMediaQuery<Theme>(t => t.breakpoints.down('md')) || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const [isContentReady, setIsContentReady] = useState<boolean>(false);

  // Initialize mobile view to show contacts list by default
  useEffect(() => {
    if (isMobile) {
      setShowChatView(false);
    }
  }, [isMobile]);

  const updateUrlFilter = (filter: FilterType | null) => {
    const params = new URLSearchParams(searchParams);
    if (filter === 'all' || filter === null) {
      params.delete('filter');
    } else {
      params.set('filter', filter);
    }
    setSearchParams(params, { replace: true });
  };

  const getFilterFromUrl = (): FilterType => {
    const filter = searchParams.get('filter') as FilterType;
    if (filter && filter in filterPresets && filter !== 'all') {
      return filter;
    }

    if (filter) {
      updateUrlFilter(null);
    }
    return 'all';
  };

  const getFilterPreset = (filter: FilterType) => {
    return filterPresets[filter] || filterPresets.all;
  };

  const currentFilter = getFilterFromUrl();
  const initialState = getFilterPreset(currentFilter);

  const [isShowAll, setIsShowAll] = useState<boolean>(initialState.isShowAll);
  const [onlyMyConversations, setOnlyMyConversations] = useState<boolean>(
    initialState.onlyMyConversations,
  );
  const [isSpam, setIsSpam] = useState<boolean>(initialState.isSpam);
  const [isInvalidContacts, setIsInvalidContacts] = useState<boolean>(
    initialState.isInvalidContacts,
  );
  const [isOptedOutContacts, setIsOptedOutContacts] = useState<boolean>(
    initialState.isOptedOutContacts,
  );
  const [isUnreadContacts, setIsUnreadContacts] = useState<boolean>(initialState.isUnreadContacts);

  const [users, setUsers] = useState<User[]>([]);
  const [customFields, setCustomFields] = useState<ContactDefaultCustomField[]>([]);
  const [messageTemplates, setMessageTemplates] = useState<MessageTemplate[]>();
  const [groups, setGroups] = useState<Group[]>([]);

  const {
    getContacts: getAllContacts,
    getContactChat,
    addMessage: createMessage,
    deleteMessage,
    updateMessage,
  } = useInboxApi();
  const getMessageTemplates = useGetMessageTemplatesApi();
  const getUsers = useGetUsersApi();
  const getCustomFields = useGetContactCustomFieldsApi();
  const { company } = useCurrentCompany();
  const { profile } = useSelector(selectProfile);

  const [allContacts, setAllContacts] = useState<{
    count: number;
    items: InboxContact[] | null;
  }>({ count: 0, items: null });

  const [contactsOffset, setContactsOffset] = useState(0);
  const [hasMoreContacts, setHasMoreContacts] = useState(false);
  const [totalContactsCount, setTotalContactsCount] = useState(0);

  const getChat = useGetInboxContactChatService();
  const setMessageAsUnread = useSetInboxMessageAsUnreadService();
  const setChatAsRead = useSetInboxChatAsReadService();
  const selectedCompanyId: string | undefined = useSelector(getCurrentCompanyId);
  const chat = useSelector(getInboxChat);
  const scheduledMessages = useSelector(getInboxScheduledMessages);
  const hasMoreMessages = useSelector(getHasMoreMessages);
  const isLoadingMoreMessages = useSelector(getIsLoadingMoreMessages);
  const dispatch = useDispatch();
  const loadMoreMessages = useLoadMoreMessagesService();
  const { getGroups } = useGroupsApi();
  const [isContactsLoading, setIsContactsLoading] = useState<boolean>(false);
  const [isChatLoading, setIsChatLoading] = useState<boolean | undefined>(undefined);
  const [group, setGroup] = useState<Group | undefined>();
  const messagesRef = useRef<HTMLDivElement>(null);
  const contactsPollInFlightRef = useRef(false);
  const chatPollInFlightRef = useRef(false);
  const [newMessage, setNewMessage] = useState<InboxNewMessage>();
  const [scheduledMessageToEdit, setScheduledMessageToEdit] =
    useState<InboxContactChatMessage | null>(null);
  const [isNewChat, setIsNewChat] = useState<boolean | undefined>(false);

  const pageLimit = 30;

  const resetScheduledState = useCallback(() => {
    dispatch(setScheduledMessages([]));
    setScheduledMessageToEdit(null);
  }, [dispatch]);

  const clearActiveChatState = useCallback(() => {
    resetScheduledState();
    dispatch(setChat(null));
  }, [dispatch, resetScheduledState]);

  const applyFilter = (filter: FilterType) => {
    const preset = getFilterPreset(filter);
    setContactsOffset(0);
    setOnlyMyConversations(preset.onlyMyConversations);
    setIsInvalidContacts(preset.isInvalidContacts);
    setIsSpam(preset.isSpam);
    setIsShowAll(preset.isShowAll);
    setIsOptedOutContacts(preset.isOptedOutContacts);
    setIsUnreadContacts(preset.isUnreadContacts);
    setSelectedContact(null);
    setSelectedContactObject(undefined);
    setShowChatView(false);
    setIsNewChat(undefined);
    setNewMessage(undefined);
    clearActiveChatState();
    updateUrlFilter(filter);
  };

  const scrollChatToBottom = () => {
    if (messagesRef.current?.scrollTop !== undefined) {
      messagesRef.current.scrollTop = messagesRef?.current?.scrollHeight;
    }
  };

  const waitForImagesAndScrollMobile = (onComplete?: () => void) => {
    if (!isMobile) {
      requestAnimationFrame(() => {
        scrollChatToBottom();
        setTimeout(() => {
          scrollChatToBottom();
          onComplete?.();
        }, 200); 
      });
      return;
    }

    // Mobile - wait for images to load before scrolling
    const attemptScroll = (attempt = 1, maxAttempts = 1) => {
      const chatContainer = messagesRef.current;
      if (!chatContainer) {
        scrollChatToBottom();
        return;
      }

      const images = chatContainer.querySelectorAll('img');

      // If no images found and we haven't tried many times, wait and retry
      if (images.length === 0) {
        if (attempt < maxAttempts) {
          setTimeout(() => attemptScroll(attempt + 1, maxAttempts), 50);
          return;
        }
        // No images after all attempts, scroll immediately
        requestAnimationFrame(() => {
          scrollChatToBottom();
          setTimeout(() => {
            onComplete?.();
          }, 100);
        });
        return;
      }

      let loadedImages = 0;
      let hasScrolled = false;
      const totalImages = images.length;

      const doScroll = () => {
        if (!hasScrolled) {
          hasScrolled = true;
          requestAnimationFrame(() => {
            scrollChatToBottom();
            setTimeout(() => {
              scrollChatToBottom();
              // Call completion callback after final scroll
              setTimeout(() => {
                onComplete?.();
              }, 100);
            }, 50);
          });
        }
      };

      // Shorter fallback timeout - scroll after 1 second if images don't load
      const fallbackTimeout = setTimeout(() => {
        doScroll();
      }, 1000);

      const onImageLoad = () => {
        loadedImages++;
        if (loadedImages >= totalImages) {
          clearTimeout(fallbackTimeout);
          // Small delay to ensure layout is complete
          setTimeout(() => {
            doScroll();
          }, 10);
        }
      };

      // Listen for load and error events on all images
      images.forEach(img => {
        if (img.complete && img.naturalHeight !== 0) {
          // Image already loaded and has dimensions
          onImageLoad();
        } else {
          img.addEventListener('load', onImageLoad, { once: true });
          img.addEventListener(
            'error',
            () => {
              onImageLoad();
            },
            { once: true },
          );
        }
      });
    };

    // Start the attempt process
    attemptScroll();
  };

  const isChatIsScrolledToBottom = () => {
    if (messagesRef.current?.scrollTop !== undefined) {
      return (
        messagesRef.current.scrollHeight -
          messagesRef.current.scrollTop -
          messagesRef.current.clientHeight <
        1
      );
    }
  };

  const fetchScheduledMessages = useCallback(
    async (
      contactId: string,
    ) => {
      if (!selectedCompanyId) {
        resetScheduledState();
        return;
      }

      try {
        const { data } = await getContactChat(
          {
            companyId: selectedCompanyId,
            contactId,
            offset: 0,
            limit: 100,
            sendStatuses: 'pending',
          },
          { scope: INBOX_CONTACT_CHAT_SCOPE },
        );

        const sortedScheduledMessages = (data.items || []).sort(
          (a: InboxContactChatMessage, b: InboxContactChatMessage) => {
            const left = a.dateScheduled ?? a.dateCreated;
            const right = b.dateScheduled ?? b.dateCreated;
            return new Date(left).getTime() - new Date(right).getTime();
          },
        );
        dispatch(setScheduledMessages(sortedScheduledMessages));

      } catch (error) {
        if (!isRequestCanceled(error)) {
          logSafeAxiosError(error);
        }
      }
    },
    [selectedCompanyId, dispatch, getContactChat],
  );

  const refreshChatWithScheduledMessages = useCallback(
    async (contactId: string) => {
      if (!selectedCompanyId) {
        return;
      }

      await Promise.all([
        getChat(selectedCompanyId, contactId, 0, 20, false, 'non_pending'),
        fetchScheduledMessages(contactId),
      ]);
    },
    [selectedCompanyId, getChat, fetchScheduledMessages],
  );

  useEffect(() => {
    if (!selectedContact || !allContacts?.items) {
      return;
    }

    const updatedContact = allContacts.items.find(contact => contact.contact.id === selectedContact);
    if (updatedContact) {
      setSelectedContactObject(updatedContact);
    }
  }, [allContacts, selectedContact]);

  const handleNewChatClick = () => {
    setIsNewChat(true);
    if (isMobile) {
      setShowChatView(true);
    }
  };

  const refreshContacts = async (callback: () => void = () => {}): Promise<void> => {
    if (!selectedCompanyId) {
      return;
    }

    const currentSelectedContactId = selectedContact;
    const searchParams: GetInboxContactsParams = search
      ? {
          query: search,
          companyId: selectedCompanyId!,
          limit: pageLimit,
          isSpam: isSpam,
          validStatuses: isInvalidContacts ? 'invalid' : 'valid',
          offset: 0,
        }
      : {
          companyId: selectedCompanyId!,
          limit: pageLimit,
          isSpam: isSpam,
          validStatuses: isInvalidContacts ? 'invalid' : 'valid',
          offset: 0,
        };

    if (onlyMyConversations && profile) {
      searchParams.assignedTo = profile.id;
    }
    if (isOptedOutContacts) {
      searchParams.contactStatuses = 'opted_out';
    } else {
      searchParams.contactStatuses = 'active';
    }
    if (isUnreadContacts) {
      searchParams.filterByRead = 'unread';
    }

    try {
      const response = await getAllContacts(searchParams, {
        scope: INBOX_CONTACTS_LIST_SCOPE,
        force: true,
      });
      setAllContacts({
        count: response.data.count,
        items: response.data.items,
      });

      if (currentSelectedContactId && response.data.items) {
        const updatedContact = response.data.items.find(
          (c: InboxContact) => c.contact.id === currentSelectedContactId,
        );
        if (updatedContact) {
          setSelectedContactObject(updatedContact);
        }
      }

      callback();
    } catch (error) {
      if (!isRequestCanceled(error)) {
        logSafeAxiosError(error);
      }
    }
  };

  const getInboxContacts = (callback: () => void = () => {}) => {
    if (!selectedCompanyId) {
      return;
    }
    const searchParams: GetInboxContactsParams = search
      ? {
          query: search,
          companyId: selectedCompanyId!,
          limit: pageLimit,
          isSpam: isSpam,
          validStatuses: isInvalidContacts ? 'invalid' : 'valid',
          offset: contactsOffset,
        }
      : {
          companyId: selectedCompanyId!,
          limit: pageLimit,
          isSpam: isSpam,
          validStatuses: isInvalidContacts ? 'invalid' : 'valid',
          offset: contactsOffset,
        };

    if (onlyMyConversations && profile) {
      searchParams.assignedTo = profile.id;
    }
    if (isOptedOutContacts) {
      searchParams.contactStatuses = 'opted_out';
    } else {
      searchParams.contactStatuses = 'active';
    }
    if (isUnreadContacts) {
      searchParams.filterByRead = 'unread';
    }
    getAllContacts(searchParams, { scope: INBOX_CONTACTS_LIST_SCOPE })
      .then(({ data }) => {
        setAllContacts(data);
        setHasMoreContacts(data.count > (allContacts.items?.length || pageLimit));
        setTotalContactsCount(data.count);
        setIsContactsLoading(false);
        if (callback) {
          callback();
        }
        // Use mobile-aware scrolling after contact data loads
        requestAnimationFrame(() => {
          waitForImagesAndScrollMobile();
        });
      })
      .catch(e => {
        if (!isRequestCanceled(e)) {
          logSafeAxiosError(e);
        }
      })
      .finally(() => {
        setIsContactsLoading(false);
      });
  };

  const fetchMoreData = () => {
    if (!selectedCompanyId) {
      return;
    }
    const searchParams = search
      ? {
          query: search,
          companyId: selectedCompanyId!,
          limit: pageLimit,
          isSpam: isSpam,
          validStatuses: isInvalidContacts ? 'invalid' : 'valid',
          offset: allContacts.items?.length ?? 0,
        }
      : {
          companyId: selectedCompanyId!,
          limit: pageLimit,
          isSpam: isSpam,
          validStatuses: isInvalidContacts ? 'invalid' : 'valid',
          offset: allContacts.items?.length ?? 0,
        };
    getAllContacts(searchParams)
      .then(({ data }) => {
        const existingIds = new Set((allContacts.items || []).map(contact => contact.contact.id));
        const newContacts = data.items.filter(
          (contact: InboxContact) => !existingIds.has(contact.contact.id),
        );

        const result = {
          count: data.count,
          items: [...(allContacts.items || []), ...newContacts],
        };

        setAllContacts(result);
        setTotalContactsCount(data.count);
        setHasMoreContacts(data.items.length === pageLimit);
        setContactsOffset(() => result.items.length);
        setIsContactsLoading(false);
        // Use mobile-aware scrolling after contact data loads
        requestAnimationFrame(() => {
          waitForImagesAndScrollMobile();
        });
      })
      .catch(e => {
        if (!isRequestCanceled(e)) {
          logSafeAxiosError(e);
        }
      })
      .finally(() => {
        setIsContactsLoading(false);
      });
  };

  useEffect(() => {
    if (!selectedCompanyId) {
      return;
    }

    setContactsOffset(0);
    setSelectedContact(null);
    setSelectedContactObject(undefined);
    clearActiveChatState();

    const messageTemplatesParams: GetMessageTemplatesParams = {
      companyId: selectedCompanyId,
      offset: 0,
      limit: 100000,
      status: 'active',
    };

    const usersQuery: GetUsersParams = {
      companyId: selectedCompanyId,
      limit: 10000,
      offset: 0,
      sort: 'last_name_asc',
    };

    getMessageTemplates(messageTemplatesParams)
      .then(data => {
        setMessageTemplates(data.data.items.filter(t => t.companyId === selectedCompanyId));
      })
      .catch(e => {
        logSafeAxiosError(e);
      });

    getUsers(usersQuery).then(response => {
      setUsers(response.data.items);
    });

    getGroups({ companyId: selectedCompanyId!, offset: 0, limit: 10000 }).then(result => {
      setGroups(result.data.items);
      const defaultGroup = result.data.items.find(g => g.type === 'default');
      if (defaultGroup) {
        setGroup(defaultGroup);
      }
    });

    getCustomFields({
      companyId: selectedCompanyId,
    }).then(result => {
      setCustomFields(result.data.items);
    });
  }, [selectedCompanyId]);

  // Filter changes: reset selection. Contacts refetch is handled by the effect below.
  useEffect(() => {
    setContactsOffset(0);
    setSelectedContact(null);
    setSelectedContactObject(undefined);
    clearActiveChatState();
  }, [onlyMyConversations, isSpam, isInvalidContacts, clearActiveChatState]);

  useEffect(() => {
    if (!selectedCompanyId) {
      return;
    }

    setContactsOffset(0);
    getInboxContacts();
  }, [
    selectedCompanyId,
    search,
    isShowAll,
    isInvalidContacts,
    isSpam,
    onlyMyConversations,
    isOptedOutContacts,
    isUnreadContacts,
  ]);

  useEffect(() => {
    const filter = getFilterFromUrl();
    const newState = getFilterPreset(filter);

    setIsShowAll(newState.isShowAll);
    setOnlyMyConversations(newState.onlyMyConversations);
    setIsSpam(newState.isSpam);
    setIsInvalidContacts(newState.isInvalidContacts);
    setIsOptedOutContacts(newState.isOptedOutContacts);
    setIsUnreadContacts(newState.isUnreadContacts);
  }, [searchParams]);

  useEffect(() => {
    if (!selectedCompanyId || !selectedContact) {
      return;
    }

    // Clear the current chat immediately when switching contacts
    clearActiveChatState();
    setIsChatLoading(true);
    setIsContentReady(false);
    chatPollInFlightRef.current = false;

    if (!selectedContactObject && allContacts) {
      const contact = allContacts?.items?.find(c => c.contact.id === selectedContact);
      setSelectedContactObject(contact);
    }

    refreshChatWithScheduledMessages(selectedContact).then(() => {
      setIsChatLoading(false);
      waitForImagesAndScrollMobile(() => {
        // Only show messages after scroll is complete
        setIsContentReady(true);
      });
    });

    const timer = setInterval(() => {
      if (chatPollInFlightRef.current) {
        return;
      }

      chatPollInFlightRef.current = true;
      const poll = isChatIsScrolledToBottom()
        ? refreshChatWithScheduledMessages(selectedContact).then(() => {
            scrollChatToBottom();
          })
        : fetchScheduledMessages(selectedContact);

      Promise.resolve(poll)
        .catch(error => {
          if (!isRequestCanceled(error)) {
            logSafeAxiosError(error);
          }
        })
        .finally(() => {
          chatPollInFlightRef.current = false;
        });
    }, 5000);

    return () => {
      clearInterval(timer);
      chatPollInFlightRef.current = false;
    };
  }, [selectedContact, fetchScheduledMessages, clearActiveChatState, refreshChatWithScheduledMessages]);

  const checkForNewMessages = useCallback(async () => {
    if (!selectedCompanyId) {
      return;
    }

    try {
      const searchParams: GetInboxContactsParams = {
        companyId: selectedCompanyId,
        limit: pageLimit,
        isSpam: isSpam,
        validStatuses: isInvalidContacts ? 'invalid' : 'valid',
        offset: 0,
      };

      if (onlyMyConversations && profile?.id) {
        searchParams.assignedTo = profile.id;
      }
      if (isOptedOutContacts) {
        searchParams.contactStatuses = 'opted_out';
      } else {
        searchParams.contactStatuses = 'active';
      }
      if (isUnreadContacts) {
        searchParams.filterByRead = 'unread';
      }
      if (search) {
        searchParams.query = search;
      }

      const response = await getAllContacts(searchParams, { scope: INBOX_CONTACTS_LIST_SCOPE });
      const newContactsData = response.data;

      if (newContactsData.items && newContactsData.items.length > 0) {
        setAllContacts(prevContacts => {
          if (!prevContacts.items) {
            return newContactsData;
          }

          let hasChanges = false;
          const contactsMap = new Map(
            prevContacts.items.map(contact => [contact.contact.id, contact]),
          );

          newContactsData.items!.forEach(newContact => {
            const existingContact = contactsMap.get(newContact.contact.id);
            if (!existingContact) {
              hasChanges = true;
            } else {
              const newLastMessageTime = new Date(newContact.lastMessageDate).getTime();
              const existingLastMessageTime = new Date(existingContact.lastMessageDate).getTime();
              if (newLastMessageTime > existingLastMessageTime) {
                hasChanges = true;
              }
            }
          });

          if (hasChanges) {
            return newContactsData;
          }

          return prevContacts;
        });
      }
    } catch (error) {
      if (!isRequestCanceled(error)) {
        logSafeAxiosError(error);
      }
    }
  }, [
    selectedCompanyId,
    isSpam,
    isInvalidContacts,
    onlyMyConversations,
    isOptedOutContacts,
    isUnreadContacts,
    search,
    profile?.id,
    getAllContacts,
  ]);

  const checkForNewMessagesRef = useRef(checkForNewMessages);
  checkForNewMessagesRef.current = checkForNewMessages;

  // Contacts poll: only re-arm on company change. Poll result must not restart the timer
  // (allContacts.items / checkForNewMessages identity used to cause request storms).
  useEffect(() => {
    if (!selectedCompanyId) {
      return;
    }

    const timer = setInterval(() => {
      if (contactsPollInFlightRef.current) {
        return;
      }

      contactsPollInFlightRef.current = true;
      Promise.resolve(checkForNewMessagesRef.current())
        .catch(error => {
          if (!isRequestCanceled(error)) {
            logSafeAxiosError(error);
          }
        })
        .finally(() => {
          contactsPollInFlightRef.current = false;
        });
    }, 5000);

    return () => {
      clearInterval(timer);
      contactsPollInFlightRef.current = false;
    };
  }, [selectedCompanyId]);

  const addMessage = async (text: string, dateScheduled?: string): Promise<void> => {
    if (!text && newMessage && newMessage.links.length === 0) {
      return;
    }

    if (selectedContact && selectedCompanyId && selectedContactObject) {
      if (selectedContactObject.contact.id !== selectedContact) {
        console.error(
          'Contact selection mismatch detected. Message sending aborted to prevent wrong recipient.',
        );
        return;
      }

      const message = { ...newMessage! };
      message.text = convertHtmlToText(text);
      message.messageType = message.links && message.links.length > 0 ? 'mms' : 'sms';
      message.contactId = selectedContactObject.contact.id;
      message.links = message.links || [];
      delete message.linksWithMimeType;

      const messageToCreate = {
        ...message,
        dateScheduled,
      };

      await createMessage(messageToCreate, selectedCompanyId).then(async () => {
        if (dateScheduled) {
          await fetchScheduledMessages(selectedContactObject.contact.id);
        } else {
            await getChat(
              selectedCompanyId,
              selectedContactObject.contact.id,
              0,
              20,
              false,
              'non_pending',
            )
        }

        requestAnimationFrame(() => {
          scrollChatToBottom();
          waitForImagesAndScrollMobile();
        });

        await checkForNewMessages();
      });
    }
  };

  const updateLinks = (link?: FileLinkWithMimeType) => {
    if (newMessage && selectedContactObject) {
      const message = { ...newMessage! };
      message.linksWithMimeType = link
        ? message.linksWithMimeType
          ? message.linksWithMimeType.concat([link])
          : [link]
        : [];
      message.links = link ? (message.links ? message.links.concat([link.url]) : [link.url]) : [];
      message.messageType = link ? 'mms' : 'sms';
      message.contactId = selectedContactObject.contact.id;
      setNewMessage(message);
    } else {
      if (
        selectedContact &&
        selectedContactObject &&
        selectedContactObject.contact.id === selectedContact
      ) {
        const newMessage: InboxNewMessage = {
          contactId: selectedContactObject.contact.id,
          text: '',
          messageType: link ? 'mms' : 'sms',
          links: link ? [link.url] : [],
          linksWithMimeType: link ? [link] : [],
        };
        setNewMessage(newMessage);
      }
    }
  };

  const onSearchContact = (search: string) => {
    setSearch(search);
  };

  const markMessageAsUnread = (message: InboxContactChatMessage) => {
    if (selectedCompanyId && selectedContact) {
      setMessageAsUnread(selectedCompanyId, [message.id]);
      const contact = allContacts?.items?.find(c => c.contact.id === message.contactId);

      if (contact) {
        contact.isChatRead = false;
      }

      getChat(selectedCompanyId, selectedContact, 0, 20, false, 'non_pending');
    }
  };

  const deleteScheduledMessage = async (message: InboxContactChatMessage) => {
    if (!selectedCompanyId || !selectedContact) {
      return;
    }

    try {
      await deleteMessage(message.id, selectedCompanyId);
      await refreshChatWithScheduledMessages(selectedContact);
      await checkForNewMessages();
    } catch (error) {
      logSafeAxiosError(error);
    }
  };

  const startReschedulingScheduledMessage = (message: InboxContactChatMessage) => {
    setScheduledMessageToEdit(message);
  };

  const clearScheduledMessageToEdit = useCallback(() => {
    setScheduledMessageToEdit(null);
  }, []);

  const updateScheduledMessage = async (
    message: InboxContactChatMessage,
    dateScheduled: string,
    text: string,
  ): Promise<void> => {
    if (!selectedCompanyId || !selectedContact) {
      return;
    }

    const nextText = convertHtmlToText(text).trim();
    if (!nextText) {
      return;
    }

    await updateMessage(
      message.id,
      {
        messageType: message.messageType,
        text: nextText,
        links: message.links || [],
        dateScheduled,
      },
      selectedCompanyId,
    );

    await refreshChatWithScheduledMessages(selectedContact);
    await checkForNewMessages();
  };

  const handleLoadMoreMessages = useCallback(async () => {
    if (!selectedCompanyId || !selectedContact || !hasMoreMessages || isLoadingMoreMessages) {
      return;
    }

    try {
      const currentMessageCount = chat?.items?.length || 0;
      await loadMoreMessages(selectedCompanyId, selectedContact, currentMessageCount, 20, 'non_pending');
    } catch (error) {
      logSafeAxiosError(error);
    }
  }, [
    selectedCompanyId,
    selectedContact,
    hasMoreMessages,
    isLoadingMoreMessages,
    chat?.items?.length,
    loadMoreMessages,
  ]);

  const selectChat = async (contactId: string | null, contactPhoneNumber?: string) => {
    if (!contactId) {
      setSelectedContact(null);
      setSelectedContactObject(undefined);
      setShowChatView(false);
      setNewMessage(undefined);
      clearActiveChatState();
      return;
    }

    if (selectedCompanyId && contactId) {
      setShowChatView(true);
      setNewMessage(undefined);
      resetScheduledState();

      let contact = allContacts?.items?.find(c => c.contact.id === contactId);
      if (!contact && contactPhoneNumber) {
        try {
          const searchParams = {
            query: contactPhoneNumber,
            companyId: selectedCompanyId,
            limit: 1,
            offset: 0,
            isSpam: false,
            validStatuses: 'valid',
          };

          const response = await getAllContacts(searchParams);
          if (response.data.items.length > 0) {
            const fetchedContact = response.data.items.find(item => item.contact.id === contactId);
            if (fetchedContact && allContacts?.items) {
              const existsInList = allContacts.items.some(item => item.contact.id === contactId);
              if (!existsInList) {
                const updatedContacts = {
                  ...allContacts,
                  items: [fetchedContact, ...allContacts.items],
                  count: allContacts.count + 1,
                };
                setAllContacts(updatedContacts);
              }
              contact = fetchedContact;
            }
          }
        } catch (error) {
          if (!isRequestCanceled(error)) {
            logSafeAxiosError(error);
          }
        }
      }

      if (contact) {
        contact.isChatRead = true;
        setSelectedContact(contact.contact.id);
      }
      setSelectedContactObject(contact);

      setIsNewChat(undefined);
      if (selectedCompanyId) {
        setChatAsRead(selectedCompanyId, [contactId]);
      }
    }
  };

  const handleBackToList = () => {
    setShowChatView(false);
    setSelectedContact(null);
    setSelectedContactObject(undefined);
    setIsNewChat(undefined);
    setNewMessage(undefined);
    clearActiveChatState();
  };

  const unreadContacts = {
    ...allContacts,
    items: allContacts?.items?.filter(c => !c.isChatRead) || null,
  };

  const editContactDrawerController = useEditContactDrawerController(refreshContacts);

  const toggleShowAll = (value: boolean) => {
    setIsShowAll(value);
    setSearch('');
    if (isMobile) {
      setShowChatView(false);
    }
  };

  return {
    isForbidden: isPageForbidden(),
    isMobile,
    showChatView,
    messagesRef,
    editContactDrawerController,
    refreshContacts,
    onlyMyConversations,
    setOnlyMyConversations,
    isSpam,
    setIsSpam,
    isInvalidContacts,
    setIsInvalidContacts,
    search,
    users,
    fetchMoreData,
    hasMoreContacts,
    totalContactsCount,
    handleNewChatClick,
    isContactsLoading,
    isShowAll,
    applyFilter: (filter: string) => applyFilter(filter as FilterType),
    toggleShowAll,
    selectedContact,
    onSearchContact,
    selectChat,
    setSelectedContactObject,
    contacts: isShowAll ? allContacts : unreadContacts,
    setContactsOffset,
    messageTemplates,
    setIsChatLoading,
    isNewChat,
    isChatLoading,
    groups,
    group,
    updateLinks,
    addMessage,
    markMessageAsUnread,
    deleteScheduledMessage,
    startReschedulingScheduledMessage,
    scheduledMessageToEdit,
    clearScheduledMessageToEdit,
    updateScheduledMessage,
    scheduledMessages,
    chat,
    selectedContactObject,
    customFields: customFields.map(field => field) as ContactDefaultCustomField[],
    isCompanyInSandBoxMode: company?.sandBoxMode || false,
    isCompanyHasSubscription: company?.subscription?.status === 'active',
    showErrorDetails: isBhiAdmin(profile),
    hideDemoFails: company?.isDemo === true,
    newMessage,
    isContentReady,
    handleBackToList,
    hasMoreMessages,
    isLoadingMoreMessages,
    handleLoadMoreMessages,
  };
};
